import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router'
import { AuthProvider, LoginPage, PasswordChangePage, useAuth } from '@/features/auth'
import { OperatorProvider } from '@/features/mission/application/OperatorContext'
import { FieldPage } from '@/features/mission/presentation/FieldPage'
import { StartPage } from '@/features/mission/presentation/StartPage'
import { appDependencies } from './dependencies'

// `networkMode: 'always'` : hors ligne, une relecture échoue tout de suite (résultat incertain affiché) au lieu
// d'attendre indéfiniment le retour du réseau.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 5_000, networkMode: 'always' },
    mutations: { networkMode: 'always' },
  },
})

function Gate() {
  const auth = useAuth()
  if (auth.status === 'loading') {
    return (
      <main className="op-screen op-screen--center">
        <p className="op-muted">Ouverture…</p>
      </main>
    )
  }
  if (!auth.session) return <LoginPage demo={appDependencies.config.dataSource === 'fixtures'} />
  if (auth.session.status === 'PASSWORD_CHANGE_REQUIRED') return <PasswordChangePage />
  if (!auth.session.canOperate) {
    return (
      <main className="op-screen op-screen--center">
        <div className="op-card op-login">
          <h1>Accès réservé aux opérateurs</h1>
          <p className="op-muted">
            Ce compte n’a pas le rôle Opérateur ou sa fiche employé est inactive. Adressez-vous au bureau.
          </p>
          <button type="button" className="field-button field-button--outline" onClick={() => void auth.signOut()}>
            Se déconnecter
          </button>
        </div>
      </main>
    )
  }
  return (
    // Une base locale par compte : `key` recrée tous les services au changement d'utilisateur.
    <OperatorProvider
      key={auth.session.userId}
      userId={auth.session.userId}
      repository={appDependencies.missions}
      geolocation={appDependencies.geolocation}
    >
      <Routes>
        <Route path="/" element={<StartPage />} />
        <Route path="/mission/:missionId" element={<FieldPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </OperatorProvider>
  )
}

// Laboratoire de simulation : chargé à part, seulement en mode fixtures avec VITE_LAB_ENABLED (gestion / test).
const LabPage = lazy(() => import('@/features/lab/LabPage'))

function Root() {
  const location = useLocation()
  if (appDependencies.config.labEnabled && location.pathname.startsWith('/labo')) {
    return (
      <Suspense
        fallback={
          <main className="op-screen op-screen--center">
            <p className="op-muted">Ouverture du laboratoire…</p>
          </main>
        }
      >
        <LabPage />
      </Suspense>
    )
  }
  return <Gate />
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider gateway={appDependencies.auth}>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
